import React, { forwardRef, useEffect, useState } from 'react';
import { ArrowRight, ChevronDown, Check, Loader2 } from 'lucide-react';
import { Popover, PopoverContent as UntypedPopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { BTN_PRIMARY } from '@/components/bento';
import { BUSINESS, COUNTRY_CODES, entityLabel } from '@/config/business';
import { businessConfig, createCompany } from '@/lib/business';

// ─── "What company do you want to start?" ─────────────────────────────────────
// The dashboard's prompt box with Company selected (Business plan §2.1): the
// same box, a sentence about the business, and two chips for the legal side,
// where it is registered and as what. The country menu starts on where the
// person is (decided on the server) and says which currency they will see.
// People who already trade take "Grow your business instead", which skips
// naming and registration.

// shadcn's forwardRef components have no prop types under checkJs.
const PopoverContent = /** @type {any} */ (UntypedPopoverContent);

// A code, not a flag emoji: Windows draws flags as two letters, and the UK's
// would read "GB" next to a menu that calls it the UK.
function CountryCode({ code }) {
  return <span className="inline-grid place-items-center rounded-md bg-zinc-800 px-1.5 py-0.5 text-[10px] font-bold tracking-wide text-zinc-300" aria-hidden="true">{code}</span>;
}

const PLACEHOLDERS = {
  start: 'e.g. Hand-dyed adire dresses and wraps, sold on Instagram and WhatsApp, delivered across Nigeria',
  grow: 'What you sell today, to whom, and how: e.g. We bake celebration cakes in Ibadan and take orders on WhatsApp',
};

// A Radix trigger (asChild) hands its child a ref to anchor the menu to. A
// plain function component drops it, and the menu has nothing to sit under.
// Typed as any for the same reason as PopoverContent above.
const Chip = /** @type {any} */ (forwardRef(function Chip(/** @type {any} */ { children, ...props }, ref) {
  return (
    <button ref={ref} type="button" {...props}
      className="inline-flex items-center gap-1.5 rounded-full border border-zinc-700 bg-zinc-900 hover:border-zinc-600 px-3 py-1.5 text-xs font-semibold text-zinc-200 transition-colors">
      {children}
      <ChevronDown className="w-3.5 h-3.5 text-zinc-500" />
    </button>
  );
}));

export default function CompanyComposer({ onCreated, currencyHint = null }) {
  const [mode, setMode] = useState('start');
  const [description, setDescription] = useState('');
  const [name, setName] = useState('');
  const [country, setCountry] = useState(null);
  const [entity, setEntity] = useState(null);
  const [currency, setCurrency] = useState(currencyHint);
  const [menu, setMenu] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    businessConfig()
      .then((cfg) => {
        if (cancelled) return;
        setCountry(c => c || cfg.suggestedCountry || 'NG');
        setCurrency(cfg.currency || 'USD');
      })
      .catch(() => { if (!cancelled) setCountry(c => c || 'NG'); });
    return () => { cancelled = true; };
  }, []);

  const c = BUSINESS.countries[country || 'NG'];
  const entities = c.entityTypes;
  const chosenEntity = entities.includes(entity) ? entity : c.defaultEntity;
  const priceNote = currency === 'NGN' ? 'Prices in naira' : 'Prices in dollars';

  const submit = async (e) => {
    e?.preventDefault?.();
    setError('');
    if (description.trim().length < 12) { setError('Tell us a little more: what you will sell, and to whom.'); return; }
    if (mode === 'grow' && name.trim().length < 2) { setError('What is your business called?'); return; }
    setBusy(true);
    try {
      const { company } = await createCompany({ description: description.trim(), country: country || 'NG', entity: chosenEntity, mode, name: name.trim() });
      onCreated?.(company);
    } catch (err) {
      setError(err.message || 'Your company could not be started.');
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-3" aria-label="Start a company">
      {mode === 'grow' && (
        <input value={name} onChange={e => setName(e.target.value)} maxLength={80} placeholder="Your business's name"
          aria-label="Your business's name"
          className="w-full rounded-2xl bg-zinc-950 border border-zinc-800 px-4 py-3 text-[15px] text-white placeholder:text-zinc-600 focus:outline-none focus:border-zinc-600" />
      )}
      {/* A size container, like the website box (PromptComposer): in a box
          under 560px the button takes a row of its own, full width. */}
      <div className="rounded-2xl bg-zinc-950 border border-zinc-800 focus-within:border-zinc-600 transition-colors [container-type:inline-size]">
        <textarea value={description} onChange={e => setDescription(e.target.value)} rows={3} maxLength={1500}
          placeholder={PLACEHOLDERS[mode]} aria-label={mode === 'grow' ? 'What your business does' : 'What company do you want to start?'}
          onKeyDown={(e) => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) submit(e); }}
          className="w-full resize-none bg-transparent px-4 pt-4 pb-2 text-[15px] leading-relaxed text-white placeholder:text-zinc-600 focus:outline-none" />
        <div className="flex flex-wrap items-center gap-2 px-3 pb-3">
          <Popover open={menu === 'country'} onOpenChange={o => setMenu(o ? 'country' : null)}>
            <PopoverTrigger asChild>
              <Chip aria-label="Where the company is registered">{country && <CountryCode code={country} />} {country ? c.short : 'Where?'}</Chip>
            </PopoverTrigger>
            <PopoverContent align="start" className="w-80 bg-zinc-950 border-zinc-800 text-zinc-200 p-1.5">
              <p className="px-2.5 pt-1.5 pb-2 text-[11px] text-zinc-500">Where the company is registered</p>
              {COUNTRY_CODES.map(code => {
                const cc = BUSINESS.countries[code];
                return (
                  <button key={code} type="button" onClick={() => { setCountry(code); setEntity(null); setMenu(null); }}
                    className={`w-full text-left rounded-xl px-2.5 py-2 hover:bg-zinc-900 flex gap-2.5 ${country === code ? 'bg-zinc-900' : ''}`}>
                    <span className="mt-0.5"><CountryCode code={code} /></span>
                    <span className="min-w-0 flex-1">
                      <span className="block text-sm font-semibold text-white">{cc.short}</span>
                      <span className="block text-[11px] text-zinc-500 leading-snug">{cc.summary}</span>
                    </span>
                    {country === code && <Check className="w-4 h-4 text-orange-400 shrink-0 mt-0.5" />}
                  </button>
                );
              })}
              <p className="px-2.5 pt-2 pb-1 text-[11px] text-zinc-500 border-t border-zinc-800 mt-1">
                {priceNote}. A company doesn't change where you pay tax.
              </p>
            </PopoverContent>
          </Popover>
          {mode === 'start' && entities.length > 1 && (
            <Popover open={menu === 'entity'} onOpenChange={o => setMenu(o ? 'entity' : null)}>
              <PopoverTrigger asChild>
                <Chip aria-label="Company type">{entityLabel(chosenEntity)}</Chip>
              </PopoverTrigger>
              <PopoverContent align="start" className="w-72 bg-zinc-950 border-zinc-800 text-zinc-200 p-1.5">
                {entities.map(id => (
                  <button key={id} type="button" onClick={() => { setEntity(id); setMenu(null); }}
                    className={`w-full text-left rounded-xl px-2.5 py-2 hover:bg-zinc-900 ${chosenEntity === id ? 'bg-zinc-900' : ''}`}>
                    <span className="block text-sm font-semibold text-white">{entityLabel(id)}</span>
                    <span className="block text-[11px] text-zinc-500">
                      {id === 'business_name' ? 'Cheaper and simpler, for a sole trader. No shares.' : 'A separate legal company. Banks and gateways prefer it.'}
                    </span>
                  </button>
                ))}
              </PopoverContent>
            </Popover>
          )}
          {mode === 'start' && entities.length === 1 && (
            <span className="rounded-full border border-zinc-800 px-3 py-1.5 text-xs font-semibold text-zinc-400">{entityLabel(chosenEntity)}</span>
          )}
          <button type="submit" disabled={busy}
            className={`ml-auto inline-flex items-center justify-center gap-2 rounded-full px-4 h-11 w-full [@container(min-width:560px)]:h-auto [@container(min-width:560px)]:w-auto [@container(min-width:560px)]:py-2 text-sm font-bold transition-colors disabled:opacity-60 ${BTN_PRIMARY}`}>
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
            {busy ? 'Reading your idea…' : mode === 'grow' ? 'Refresh my business' : 'Build my company'}
            {!busy && <ArrowRight className="w-4 h-4" />}
          </button>
        </div>
      </div>
      {error && <p role="alert" className="text-sm text-red-400">{error}</p>}
      <div className="flex flex-wrap items-center justify-between gap-2 text-xs">
        <p className="text-zinc-400">
          {mode === 'grow' ? 'Free preview: brand, website, ads and a 30-day plan.' : 'Free preview. You only pay when you decide to register.'}
        </p>
        <button type="button" onClick={() => { setMode(m => (m === 'grow' ? 'start' : 'grow')); setError(''); }}
          className="font-semibold text-orange-400 hover:text-orange-300">
          {mode === 'grow' ? 'Starting something new? Start a company' : 'Already trading? Grow your business instead'}
        </button>
      </div>
    </form>
  );
}
